// core/api/useDepartment.ts
import { useState } from "react";
import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { useAuthToken } from "../data/redux/useAuthToken";
import { toast } from "react-toastify";

const DEPARTMENT_BASE_URL = `${API_BASE_PATH}/department/`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

const useDepartment = (_token: string | null) => {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const empToken = useAuthToken();

  const headers = {
    "Content-Type": "application/json",
    authorization: "Bearer " + _token,
  };

  const fetchDepartments = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${DEPARTMENT_BASE_URL}`, { headers });
      const normalized = res.data.map((item: any) => ({
        department_name: item["Department"],
        employee_count: item["No of Employee"],
        status: item["Status"]?.toLowerCase() || "inactive",
      }));
      setDepartments(normalized);
    } catch (err: any) {
      console.error("[Fetch Departments] Error:", err);
      toast.error(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const addDepartment = async (data: any) => {
    try {
      const res = await axios.post(`${DEPARTMENT_BASE_URL}`, data, { headers });
      toast.success("Department added successfully");
      return res.data;
    } catch (err: any) {
      console.error("[Add Department] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  const updateDepartment = async (name: string, data: any) => {
    try {
      const encodedName = encodeURIComponent(name);
      const url = `${DEPARTMENT_BASE_URL}/${encodedName}`;
      const res = await axios.put(url, data, { headers });
      toast.success("Department updated successfully");
      return res.data;
    } catch (err: any) {
      console.error("[Update Department] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  const deleteDepartment = async (name: string) => {
    try {
      const encoded = encodeURIComponent(name);
      const url = `${DEPARTMENT_BASE_URL}/departments/${encoded}?department_name=${encoded}`;
      const res = await axios.delete(url, { headers });
      toast.success("Department deleted successfully");
      return res.data;
    } catch (err: any) {
      console.error("[Delete Department] Error:", err);
      toast.error(extractErrorMessage(err));
      throw err;
    }
  };

  const fetchDepartmentNames = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${DEPARTMENT_BASE_URL}`, { headers });
      const departmentNames = res.data.map((item: any) => item["Department"]);
      return departmentNames;
    } catch (err: any) {
      console.error("[Fetch Department Names] Error:", err);
      toast.error(extractErrorMessage(err));
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getNormalizedDepartments = async () => {
    try {
      const res = await axios.get(`${DEPARTMENT_BASE_URL}`, { headers });
      const normalized = res.data.map((item: any) => ({
        department_name: item["Department"],
        employee_count: item["No of Employee"],
        status: item["Status"]?.toLowerCase() || "inactive",
      }));
      return normalized;
    } catch (err: any) {
      console.error("[Get Normalized Departments] Error:", err);
      toast.error(extractErrorMessage(err));
      return [];
    }
  };

  return {
    departments,
    isLoading,
    fetchDepartments,
    addDepartment,
    getNormalizedDepartments,
    updateDepartment,
    deleteDepartment,
    fetchDepartmentNames,
  };
};
export default useDepartment;
